import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Typography, Button, Menu, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DocumentTypeBadge from '../../../../components/Documents/DocumentTypeBadge';
import SectionCard from '../ui/SectionCard';
import { formatDate, formatValue } from '../../utils/format';

const DOC_TYPE_OPTIONS = [
  { type: 'quotation', label: 'ใบเสนอราคา (QT)' },
  { type: 'delivery_note', label: 'ใบส่งงาน (DN)' },
  { type: 'billing_note', label: 'ใบวางบิล (BL)' },
  { type: 'receipt', label: 'ใบเสร็จรับเงิน (REC)' },
  { type: 'tax_invoice', label: 'ใบแจ้งหนี้/ใบกำกับภาษี (INV)' },
];

const STATUS_CHIP = {
  draft: { label: 'ร่าง', color: '#6b7280', bg: '#f3f4f6' },
  sent: { label: 'ส่งแล้ว', color: '#2563eb', bg: '#eff6ff' },
  approved: { label: 'อนุมัติ', color: '#16a34a', bg: '#f0fdf4' },
  rejected: { label: 'ปฏิเสธ', color: '#dc2626', bg: '#fef2f2' },
  cancelled: { label: 'ยกเลิก', color: '#9ca3af', bg: '#f9fafb' },
};

// เอกสารฝั่ง Sale (QT/DN/BL/REC/INV) ที่สร้างจาก opportunity นี้โดยตรง — สร้างเป็น
// modal popup ที่นี่เลย (ผูก opportunity_id ให้อัตโนมัติ ไม่ navigate ไปหน้าเต็มอีกต่อไป)
// แล้วย้อนกลับมาดูรายการได้ที่การ์ดนี้ (คลิกแถวเพื่อดู/แก้ไขเอกสารที่มีอยู่แล้วยัง
// เปิดหน้าเต็มตามเดิม — เป็นคนละ flow กับการสร้างใหม่)
export function SaleDocumentsCard({ documents, loading, onCreateDocument }) {
  const history = useHistory();
  const [createAnchor, setCreateAnchor] = useState(null);

  const goCreate = (type) => {
    setCreateAnchor(null);
    onCreateDocument(type);
  };

  return (
    <SectionCard
      icon={<DescriptionOutlinedIcon />}
      title="เอกสาร"
      accentColor="secondary.main"
      action={(
        <Button
          variant="contained" size="small" startIcon={<AddIcon />} endIcon={<ArrowDropDownIcon />}
          onClick={(e) => setCreateAnchor(e.currentTarget)}
          sx={{ borderRadius: 2 }}
        >
          สร้างเอกสาร
        </Button>
      )}
    >
      <Menu
        anchorEl={createAnchor}
        open={Boolean(createAnchor)}
        onClose={() => setCreateAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {DOC_TYPE_OPTIONS.map(({ type, label }) => (
          <MenuItem key={type} onClick={() => goCreate(type)}>{label}</MenuItem>
        ))}
      </Menu>

      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>กำลังโหลด...</Typography>
      ) : (documents || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>ยังไม่มีเอกสาร</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: 0, color: 'text.secondary', fontSize: '0.75rem' }}>ประเภท</TableCell>
                <TableCell sx={{ border: 0, color: 'text.secondary', fontSize: '0.75rem' }}>เลขที่เอกสาร</TableCell>
                <TableCell sx={{ border: 0, color: 'text.secondary', fontSize: '0.75rem' }}>วันที่</TableCell>
                <TableCell sx={{ border: 0, color: 'text.secondary', fontSize: '0.75rem', textAlign: 'right' }}>มูลค่า</TableCell>
                <TableCell sx={{ border: 0, color: 'text.secondary', fontSize: '0.75rem' }}>สถานะ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => {
                const st = STATUS_CHIP[doc.status] || STATUS_CHIP.draft;
                return (
                  <TableRow
                    key={doc.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => history.push(`/sale/documents/${doc.id}`)}
                  >
                    <TableCell><DocumentTypeBadge type={doc.document_type} /></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{doc.document_number}</TableCell>
                    <TableCell>{formatDate(doc.date)}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{formatValue(doc.total)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={st.label} sx={{ color: st.color, bgcolor: st.bg, fontWeight: 600 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

export default SaleDocumentsCard;
